import { useState } from 'react';
import EmbedBlock from '../EmbedBlock';
import { addAutoplay, isHttpsUrl, normalizeEmbedUrl } from '../../model/pages';

/** The player URL the poster swaps in: normalized + the autoplay flag. Pure. */
export function withAutoplay(url: string): string {
  return addAutoplay(normalizeEmbedUrl(url));
}

/** A video "facade": the poster frame with a play button, swapped for the real
 *  player on click. Faster than mounting an iframe on load, and it lets a
 *  template show a real-looking video before the owner adds one: with no
 *  `url` the poster stays a still (the play button is decorative). */
export default function VideoPoster({ url, poster, title, frame, accent, onAccent, disabled }: {
  url?: string; poster: string; title?: string;
  /** Frame classes (radius / ring / shadow) from the page style. */
  frame: string;
  accent: string; onAccent: string;
  /** Editor canvas / previews: never swap in the player. */
  disabled?: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const playable = !!url && isHttpsUrl(normalizeEmbedUrl(url)) && !disabled;
  if (playing && playable) {
    return <div className={`overflow-hidden ${frame}`}><EmbedBlock url={url} title={title || 'Video'} autoplay /></div>;
  }
  return (
    <button type="button" onClick={playable ? () => setPlaying(true) : undefined}
      aria-label={playable ? `Play video${title ? `: ${title}` : ''}` : undefined}
      tabIndex={playable ? 0 : -1}
      className={`group relative block w-full overflow-hidden ${playable ? 'cursor-pointer' : 'cursor-default'} ${frame}`}>
      <img src={poster} alt="" decoding="async" className="aspect-video w-full object-cover" />
      <span aria-hidden className="absolute inset-0 bg-black/15 transition-colors group-hover:bg-black/5" />
      <span aria-hidden className="absolute left-1/2 top-1/2 grid h-[18%] min-h-12 w-auto aspect-square -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full shadow-[0_10px_30px_-8px_rgba(0,0,0,0.45)] transition-transform duration-200 group-hover:scale-105"
        style={{ background: accent, color: onAccent }}>
        <svg viewBox="0 0 24 24" className="ml-[8%] h-[38%] w-[38%]" fill="currentColor" aria-hidden><path d="M7 4.6v14.8a1 1 0 0 0 1.5.86l12.2-7.4a1 1 0 0 0 0-1.72L8.5 3.74A1 1 0 0 0 7 4.6z" /></svg>
      </span>
    </button>
  );
}
